// Der Weg hinein – gleich für jede App (PLAN.md, Abschnitt 5, E1 „Dein Test“):
//
//   Start ──► „Ich habe einen Einladungscode“ ──► Code ──► „Hallo Vera“ ──► Face ID ──► drin
//         └─► „Mit Passkey anmelden“ ──► Face ID ──► drin (oder: Gerät wartet aufs Büro)
//
// Ist man drin, zeigt die Komponente die App. Ohne Empfang startet die App
// mit dem, was sie beim letzten Mal über den Nutzer wusste – draußen im
// Keller muss sie trotzdem aufgehen (PLAN.md, Abschnitt 3, „Offline zuerst“).
// Seit E2: Meldet das Gerät selbst „kein Netz“, fragt sie gar nicht erst
// nach; kommt das Netz zurück, fragt sie von selbst neu. Seit E2, Schritt 4
// steht der Test-Balken schon hier – die Umgebung liest die App dafür aus
// der Adresse (adresse.ts), weil der Server sie erst nach der Anmeldung sagt.
//
// Seit 22.10.2026 (O59): Am iPhone oder iPad im Browser steht vor dem Code
// ein kleines Schild „Zuerst aufs iPad holen: Teilen → Zum Home-Bildschirm“.
// Dort haben Browser und installierte App getrennten Speicher – ein Code,
// in Safari eingegeben, gibt nur Safari frei, und das Symbol wäre ein
// zweites Gerät (Manuel am iPad, 22.10.2026). Am Laptop, in der
// installierten App und auf Android: kein Schild.

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { einladungscodeLesen, type AppName, type IchAntwort } from "@happy-energy/kern";
import { ApiFehler, holen, ohneNetz, OhneVerbindung } from "./api.ts";
import { abmelden, einladungPruefen, passkeyAnmelden, passkeyEinrichten, passkeyMeldung, passkeyMoeglich } from "./passkey.ts";
import { umgebungAusAdresse } from "./adresse.ts";
import { TestBand } from "./bausteine.tsx";
import { Ikon } from "./Ikon.tsx";

type Zustand =
  | { art: "laden" }
  | { art: "start"; meldung?: string }
  | { art: "code"; meldung?: string }
  | { art: "einrichten"; name: string; meldung?: string }
  | { art: "wartet"; geraetName?: string }
  | { art: "gesperrt"; meldung: string }
  | { art: "drin"; ich: IchAntwort; ohneVerbindung: boolean };

const SPEICHER = "he-ich";

function gemerkt(): IchAntwort | null {
  try {
    const roh = localStorage.getItem(SPEICHER);
    return roh ? (JSON.parse(roh) as IchAntwort) : null;
  } catch {
    return null;
  }
}

function merken(ich: IchAntwort | null): void {
  try {
    if (ich) localStorage.setItem(SPEICHER, JSON.stringify(ich));
    else localStorage.removeItem(SPEICHER);
  } catch {
    // Privater Modus oder voller Speicher – dann eben ohne Gedächtnis.
  }
}

/**
 * Läuft die Seite am iPhone oder iPad im Browser statt als App vom
 * Home-Bildschirm? Dann welches Gerät – sonst null. Dieselbe Erkennung wie
 * `inSafariStattApp` in der Doku-App (ein iPad meldet sich als „Macintosh“
 * mit Berührung).
 */
function iosImBrowser(): "iPhone" | "iPad" | null {
  const ua = navigator.userAgent;
  const ipad = /iPad/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (!ipad && !/iPhone|iPod/.test(ua)) return null;
  const installiert = window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return installiert ? null : ipad ? "iPad" : "iPhone";
}

/** Das Schild vor dem Code: erst aufs Gerät holen, dann dort anmelden – höchstens zwei Zeilen. */
function ZumHomeBildschirm() {
  const geraet = iosImBrowser();
  if (!geraet) return null;
  return (
    <p className="heim-schild">
      <b>Zuerst aufs {geraet} holen:</b> Teilen <Ikon name="teilen" groesse="k" /> → „Zum Home-Bildschirm“, dann die App dort öffnen und anmelden.
    </p>
  );
}

/** Ohne Verbindung: weiter mit dem gemerkten Stand – oder, ohne ihn, zum Start. */
function ohneVerbindungWeiter(): Zustand {
  const ich = gemerkt();
  return ich ? { art: "drin", ich, ohneVerbindung: true } : { art: "start", meldung: "Keine Verbindung zum Server." };
}

/** Fragt den Server, wer wir sind, und übersetzt die Antwort in einen Zustand. */
async function nachfragen(): Promise<Zustand> {
  // Flugmodus, Keller: gar nicht erst fragen.
  if (ohneNetz()) return ohneVerbindungWeiter();
  try {
    const ich = await holen<IchAntwort>("/api/ich");
    merken(ich);
    return { art: "drin", ich, ohneVerbindung: false };
  } catch (fehler) {
    // Auch eine Antwort, die kein JSON ist (Anmeldeportal im Hotel-WLAN), heißt:
    // der Server war nicht zu erreichen – nicht: abgemeldet.
    if (fehler instanceof OhneVerbindung || (fehler instanceof ApiFehler && (fehler.status >= 500 || fehler.keinJson))) {
      return ohneVerbindungWeiter();
    }
    if (fehler instanceof ApiFehler) {
      if (fehler.grund === "geraet-wartet") {
        const g = fehler.daten["geraet"] as { name?: string } | undefined;
        return { art: "wartet", ...(g?.name ? { geraetName: g.name } : {}) };
      }
      if (fehler.grund === "geraet-gesperrt" || fehler.grund === "nutzer-gesperrt") {
        merken(null);
        return { art: "gesperrt", meldung: fehler.message };
      }
    }
    merken(null);
    return { art: "start" };
  }
}

export interface AnmeldungProps {
  app: AppName;
  appName: string;
  children: (ich: IchAntwort, hilfen: AnmeldungHilfen) => ReactNode;
}

export interface AnmeldungHilfen {
  abmelden: () => void;
  ohneVerbindung: boolean;
  /** Den Server neu fragen, wer wir sind – etwa wenn er beim Senden „nicht angemeldet“ sagt. */
  neuFragen: () => void;
}

export function Anmeldung({ app, appName, children }: AnmeldungProps) {
  const [zustand, setZustand] = useState<Zustand>({ art: "laden" });
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const [code, setCode] = useState("");

  // Jedes Hochzählen fragt den Server neu, wer wir sind.
  const [abfrage, setAbfrage] = useState(0);
  const neuFragen = useCallback(() => setAbfrage((n) => n + 1), []);

  useEffect(() => {
    let gilt = true;
    void nachfragen().then((z) => {
      if (gilt) setZustand(z);
    });
    return () => {
      gilt = false;
    };
  }, [abfrage]);

  // Gestartet ohne Verbindung: Kommt das Netz zurück, gleich neu fragen.
  const ohneVerbindungDrin = zustand.art === "drin" && zustand.ohneVerbindung;
  useEffect(() => {
    if (!ohneVerbindungDrin) return;
    window.addEventListener("online", neuFragen);
    return () => window.removeEventListener("online", neuFragen);
  }, [ohneVerbindungDrin, neuFragen]);

  // Wartet das Gerät auf das Büro, fragt die App alle zehn Sekunden nach.
  useEffect(() => {
    if (zustand.art !== "wartet") return;
    const uhr = setInterval(neuFragen, 10_000);
    return () => clearInterval(uhr);
  }, [zustand.art, neuFragen]);

  async function mitPasskeyAnmelden() {
    setBeschaeftigt(true);
    try {
      await passkeyAnmelden();
      setZustand(await nachfragen());
    } catch (fehler) {
      setZustand({ art: "start", meldung: passkeyMeldung(fehler) });
    } finally {
      setBeschaeftigt(false);
    }
  }

  async function codePruefen() {
    const gelesen = einladungscodeLesen(code);
    if (!gelesen) {
      setZustand({ art: "code", meldung: "Der Code hat zehn Zeichen, zum Beispiel K7M2Q-9XA4T." });
      return;
    }
    setBeschaeftigt(true);
    try {
      const name = await einladungPruefen(gelesen);
      setZustand({ art: "einrichten", name });
    } catch (fehler) {
      setZustand({ art: "code", meldung: passkeyMeldung(fehler) });
    } finally {
      setBeschaeftigt(false);
    }
  }

  async function einrichten(name: string) {
    setBeschaeftigt(true);
    try {
      await passkeyEinrichten();
      setZustand(await nachfragen());
    } catch (fehler) {
      setZustand({ art: "einrichten", name, meldung: passkeyMeldung(fehler) });
    } finally {
      setBeschaeftigt(false);
    }
  }

  async function raus() {
    try {
      await abmelden();
    } finally {
      merken(null);
      setZustand({ art: "start" });
    }
  }

  if (zustand.art === "drin") {
    return <>{children(zustand.ich, { abmelden: () => void raus(), ohneVerbindung: zustand.ohneVerbindung, neuFragen })}</>;
  }

  const [vorne, hinten] = appName.startsWith("Happy Energy ") ? ["Happy Energy", appName.slice(13)] : [appName, ""];

  return (
    <>
      <TestBand umgebung={umgebungAusAdresse(window.location.hostname)} />
      <div className="anmeldung">
        <div className="anmeldung-kasten">
          <img src="/symbol-192.png" alt="" className="anmeldung-zeichen" />
          <h1>
            {vorne} <span>{hinten}</span>
          </h1>

          {zustand.art === "laden" && <p className="leise">Einen Moment …</p>}

          {zustand.art === "start" && (
            <>
              <p className="leise">{app === "doku" ? "Die Besichtigung – Schritt für Schritt." : "Das Büro von Happy Energy."}</p>
              <ZumHomeBildschirm />
              {zustand.meldung && <div className="meldung fehler">{zustand.meldung}</div>}
              {!passkeyMoeglich() && (
                <div className="meldung fehler">Dieser Browser kann keine Passkeys. Bitte Safari am iPhone, Chrome oder Edge verwenden.</div>
              )}
              <button onClick={() => void mitPasskeyAnmelden()} disabled={beschaeftigt || !passkeyMoeglich()}>
                Anmelden
              </button>
              <button className="zweit dunkel" onClick={() => setZustand({ art: "code" })} disabled={beschaeftigt}>
                Ich habe einen Einladungscode
              </button>
              <p className="klein-text">Ohne Passwort – mit Face ID, Fingerabdruck oder dem Code deines Geräts.</p>
            </>
          )}

          {zustand.art === "code" && (
            <>
              <ZumHomeBildschirm />
              <p className="leise">Den Code bekommst du im Büro. Er gilt sieben Tage und nur einmal.</p>
              <label htmlFor="code">Einladungscode</label>
              <input
                id="code"
                className="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="XXXXX-XXXXX"
                autoComplete="one-time-code"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                maxLength={13}
              />
              {zustand.meldung && <div className="meldung fehler">{zustand.meldung}</div>}
              <button onClick={() => void codePruefen()} disabled={beschaeftigt || code.trim().length < 10}>
                Weiter
              </button>
              <button className="zweit dunkel" onClick={() => setZustand({ art: "start" })} disabled={beschaeftigt}>
                Zurück
              </button>
            </>
          )}

          {zustand.art === "einrichten" && (
            <>
              <p className="gross">Hallo {zustand.name.split(" ")[0]}!</p>
              <p className="leise">
                Jetzt richtest du deinen Zugang ein. Dein Gerät fragt gleich nach Face ID, Fingerabdruck oder seinem Code – so wie beim
                Entsperren. Danach bist du angemeldet und musst das im Alltag nicht mehr tun.
              </p>
              {zustand.meldung && <div className="meldung fehler">{zustand.meldung}</div>}
              <button onClick={() => void einrichten(zustand.name)} disabled={beschaeftigt}>
                Zugang einrichten
              </button>
            </>
          )}

          {zustand.art === "wartet" && (
            <>
              <p className="gross">Fast geschafft</p>
              <p className="leise">
                Dieses Gerät{zustand.geraetName ? ` („${zustand.geraetName}“)` : ""} ist neu. Das Büro muss es einmal freigeben – sag dort
                kurz Bescheid. Die App fragt von selbst nach und geht auf, sobald es so weit ist.
              </p>
              <button className="zweit dunkel" onClick={neuFragen}>
                Jetzt nachsehen
              </button>
            </>
          )}

          {zustand.art === "gesperrt" && (
            <>
              <div className="meldung fehler">{zustand.meldung}</div>
              <button className="zweit dunkel" onClick={neuFragen}>
                Noch einmal versuchen
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
